/**
 * thumbUrl(original, width) — Wave 5.9 build 13.1 (resilient fallback).
 *
 * Smart image URL wrapper. Accepts EITHER a string URL OR a photo dict
 * (backend punch item shape: `{url, thumb_url, type, filename, ...}`).
 *
 * Fallback chain (in priority order):
 *   1. `photo.thumb_url`     — pre-baked 512px thumb, same-origin,
 *                              query-param-free (build 13 upload path)
 *   2. `photo.url + ?w=<n>`  — backend's on-demand /api/uploads/…?w=
 *                              or /api/media/thumb proxy (legacy)
 *   3. `photo.url` (raw)     — original bytes, resolves via <img onError>
 *   4. inline SVG placeholder — never render as blank
 *
 * A photo NEVER renders as nothing because a preferred field is missing.
 * `onThumbError()` walks steps 2 → 3 → 4 automatically if the initial
 * fetch fails (e.g. thumb hasn't been backfilled on prod yet).
 */

// 1×1 transparent-ish "photo unavailable" SVG. Kept tiny so it inlines
// cheaply in an `<img src=…>` attribute.
const _PLACEHOLDER_DATA_URI = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">'
  + '<rect width="40" height="40" fill="#e5e7eb"/>'
  + '<path d="M11 27l5-6 4 4 4-5 5 7z" fill="#9ca3af"/>'
  + '<circle cx="15" cy="15" r="2.5" fill="#9ca3af"/>'
  + '</svg>'
);

// Resolve backend origin at MODULE LOAD, with a safe runtime fallback.
// `process.env.REACT_APP_BACKEND_URL` is CRA's DefinePlugin substitution;
// if a chunk was built before .env was populated (or during hot reload
// races), it can arrive as ''. In that case use the current window origin.
function _backendOrigin() {
  const fromEnv = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return '';
}

function _originHost(origin) {
  try { return new URL(origin).host; } catch { return ''; }
}

function _coerceToString(original) {
  if (!original) return '';
  if (typeof original === 'string') return original;
  if (typeof original === 'object') {
    return original.url || original.src || original.href || '';
  }
  return '';
}

export function thumbUrl(original, width = 300) {
  // Wave 5.9 build 13 (Feb 2026) — if the caller passes a photo dict
  // with a pre-generated `thumb_url` (baked at upload time by
  // `services.storage.upload_image_with_thumbnail`), prefer it. Thumb
  // URLs are same-origin, query-param-free, and stable across sessions —
  // the SW image cache actually hits on repeat visits (unlike the old
  // `/api/media/thumb?u=…&t=<rotating_jwt>` shape).
  if (original && typeof original === 'object') {
    const pre = original.thumb_url;
    if (typeof pre === 'string' && pre) {
      // Relative same-origin path — return as-is for the browser to
      // resolve against location.origin. If somehow a full URL was
      // stored (external thumb), return that too.
      return pre;
    }
  }

  const raw = _coerceToString(original);
  if (!raw) return _PLACEHOLDER_DATA_URI;

  const backend = _backendOrigin();
  const backendHost = _originHost(backend);

  // Build the absolute URL (or a best-effort absolute) so we can decide
  // origin. Relative /api/uploads/... paths get prefixed with backend.
  const abs = raw.startsWith('http')
    ? raw
    : `${backend}${raw.startsWith('/') ? '' : '/'}${raw}`;

  // Fast path for /api/uploads/... — use the backend native thumbnail
  // pipeline. Works even if URL parsing fails (naive check).
  //
  // 2026-07-15 edge-cache leak fix — param renamed from `w` → `tw`
  // (thumb-width) so every previously edge-cached `?w=300` URL becomes
  // unreachable through this app. New URLs return `Cache-Control:
  // private, max-age=300, must-revalidate`; edge caches never keep them.
  const isUploadsPath = raw.startsWith('/api/uploads/') || abs.includes('/api/uploads/');
  if (isUploadsPath) {
    const sep = abs.includes('?') ? '&' : '?';
    return `${abs}${sep}tw=${width}`;
  }

  // Same-origin non-uploads path — return as-is (no proxy).
  try {
    const parsed = new URL(abs);
    if (backendHost && parsed.host === backendHost) {
      return abs;
    }
  } catch {
    // Fall through — treat as external.
  }

  // External URL — SSRF-hardened proxy with auth query param.
  // 2026-07-15: `w` param kept for the media proxy (it's an internal
  // shape, never edge-cached now that _CACHE_HEADERS is private-only).
  let query = `u=${encodeURIComponent(abs)}&w=${width}`;
  try {
    const cs = sessionStorage.getItem('contractor_session_token')
      || localStorage.getItem('contractor_session_token');
    const jwt = localStorage.getItem('accessToken')
      || localStorage.getItem('access_token');
    if (cs) {
      query += `&cs=${encodeURIComponent(cs)}`;
    } else if (jwt) {
      query += `&t=${encodeURIComponent(jwt)}`;
    } else {
      // No credentials — hand back the original URL rather than a 401.
      return abs;
    }
  } catch {
    return abs;
  }

  return `${backend}/api/media/thumb?${query}`;
}


export const PHOTO_PLACEHOLDER = _PLACEHOLDER_DATA_URI;

/**
 * onThumbError — <img onError> that walks the fallback chain in place.
 *
 * Given a photo dict OR raw URL, on each failure it advances one step:
 *   src=<thumb_url>       → src=<url>?w=…        (backend on-demand thumb)
 *   src=<url>?w=…         → src=<url> raw        (original bytes)
 *   src=<url>             → src=<placeholder svg>  (never blank)
 *
 * Uses `dataset._fbStep` to track progress so a chain of failures
 * doesn't loop. Never removes the <img> from the DOM — the user asked
 * that a missing photo must never render as nothing.
 */
export function onThumbError(original) {
  return (e) => {
    const img = e.target;
    if (!img) return;
    // Read the raw url (string) — supports both dict + string input.
    const raw = _coerceToString(original);
    const backend = _backendOrigin();
    const abs = raw
      ? (raw.startsWith('http') ? raw : `${backend}${raw.startsWith('/') ? '' : '/'}${raw}`)
      : '';
    const step = Number(img.dataset._fbStep || '0');

    // Step 0 → try backend-generated ?tw= thumb (only useful for
    // /api/uploads/... — external URLs go straight to raw).
    if (step === 0 && abs) {
      img.dataset._fbStep = '1';
      const isUploadsPath = raw.startsWith('/api/uploads/') || abs.includes('/api/uploads/');
      if (isUploadsPath) {
        const sep = abs.includes('?') ? '&' : '?';
        const next = `${abs}${sep}tw=300`;
        if (img.src !== next) { img.src = next; return; }
      }
      // Not an uploads path — skip directly to raw.
      if (img.src !== abs) { img.src = abs; return; }
    }

    // Step 1 → try raw URL.
    if (step <= 1 && abs && img.src !== abs) {
      img.dataset._fbStep = '2';
      img.src = abs;
      return;
    }

    // Step 2+ → placeholder (never blank).
    img.dataset._fbStep = '3';
    if (img.src !== _PLACEHOLDER_DATA_URI) {
      img.src = _PLACEHOLDER_DATA_URI;
    }
  };
}
